<template>
    <v-container v-if="profile" fluid class="pa-0">
        <!-- Admin State Control -->
        <v-card v-if="$rc.$state.isAuthActorSystemAdmin()" class="mb-4" outlined>
            <v-card-title class="pb-2 mb-2">
                <v-icon left color="primary">mdi-cog</v-icon>
                Admin Controls
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="4">
                        <m-restricted-field>
                            <v-select
                                label="State"
                                :items="$rc.enumChoices('numbering_plan_state', false)"
                                :value="profile.numbering_plan.state"
                                outlined
                                dense
                                v-on:change="onChangeState"
                            />
                        </m-restricted-field>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Plan Content -->
        <v-card class="mb-4" outlined>
            <v-card-title class="pb-2">
                <v-icon left color="primary">mdi-phone</v-icon>
                Numbering Plan
                <v-spacer />
                <m-restricted-button 
                    v-if="$rc.$state.isAuthActorSystemAdmin()"
                    small
                    outlined
                    v-on:click="onClickEditPlan"
                >
                    <v-icon left small>mdi-pencil</v-icon>
                    Edit Plan
                </m-restricted-button>
            </v-card-title>

            <v-card-text>
                <div
                    class="plan-content"
                    v-html="plan_body"
                >
                </div>
            </v-card-text>
        </v-card>

        <!-- Hosted Messaging Configuration -->
        <v-card v-if="$rc.$state.isAuthActorSystemAdmin()" class="mb-4" outlined>
            <v-card-title class="pb-2">
                <v-icon left color="primary">mdi-message-text</v-icon>
                Messaging Configuration
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12">
                        <v-switch
                            label="Hosting messaging (RedChirp enabling texting on existing landlines)"
                            v-model="profile.numbering_plan.hosted_messaging"
                            color="primary"
                            v-on:change="save"
                            hide-details
                        />
                    </v-col>
                    <v-col v-if="profile.numbering_plan.hosted_messaging" cols="12" md="6" class="mt-3">
                        <v-select
                            label="LOA Signature State"
                            :items="$rc.enumChoices('numbering_plan_loa_state', false)"
                            v-model="profile.numbering_plan.hosted_messaging_loa_state"
                            outlined
                            dense
                            v-on:change="save"
                        />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Organization Event -->
        <v-card class="mb-4" outlined>
            <v-card-title class="pb-2">
                <v-icon left color="primary">mdi-calendar-check</v-icon>
                Events
            </v-card-title>
            
            <v-card-text>
                <m-organization-event-card
                    :value="profile.event_numbering_plan"
                    v-on:update="$emit('update')"
                />
            </v-card-text>
        </v-card>

        <v-alert
            v-if="profile.numbering_plan.hosted_messaging && profile.numbering_plan.state != 'complete'"
            type="info"
            outlined
            class="mb-4"
        >
            <v-icon slot="prepend" class="align-self-start mr-2">mdi-information</v-icon>
            This section will remain un-checked even after you e-sign the Letter of Authorization above because our team
            still has some work to do! We will mark this section as complete once we order the services, receive them
            (which takes 24-48 hours), and fully test them for you.
        </v-alert>

    </v-container>
</template>

<script>
export default {
    props: {
        profile: {
            type: Object,
            default: function () { return {}; },
        },
    },

    methods: {
        onClickEditPlan() {
            this.$rc.prompt({
                'title': 'Edit phone number plan',
                'label': 'Plan',
                'value': this.plan_body_plain_text,
                'textarea': true,
                'allow_blank': true,
                'after_prompt': (value) => {
                    this.doUpdatePlan(value);
                }
            });
        },

        doUpdatePlan(value) {
            this.profile.numbering_plan.body = value;
            this.save();
        },

        onChangeState(new_state) {
            this.profile.numbering_plan.state = new_state;
            this.save();
        },

        save() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/welcome/numbering-plan',

                'params': this.profile.numbering_plan,

                'on_success': (data) => {
                    this.dialog = false;
                    this.$emit('update-profile', data);
                },

                'on_error': 'Unable to save numbering plan',
            });
        },
    },

    computed: {
        hide_plan() {
            if (this.profile == null)
                return false;

            if (this.profile.numbering_plan.state != 'in-progress') return false;
            if (this.profile.numbering_plan.body == null) return false;

            if (this.profile.numbering_plan.body.toLowerCase().includes('calendly'))
                return true;

            return false;
        },

        plan_body() {
            let body = this.$rc.getDeep(this.profile, 'numbering_plan/body');

            if (body == null || body.trim().length == 0) {
                return 'No plan entered';
            }

            return this.$rc.wrapHTML(body);
        },

        plan_body_plain_text() {
            let body = this.$rc.getDeep(this.profile, 'numbering_plan/body');

            if (body == null || body.trim().length == 0) {
                return '';
            }

            return body;
        },
    },
}
</script>

<style lang="scss" scoped>
.plan-content {
    line-height: 1.6;
    font-size: 0.875rem;
    
    :deep(p) {
        margin-bottom: 1rem;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
    
    :deep(ul), :deep(ol) {
        padding-left: 1.5rem;
        margin-bottom: 1rem;
    }
    
    :deep(li) {
        margin-bottom: 0.5rem;
    }
}

.v-card {
    transition: box-shadow 0.3s ease;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08) !important;
    }
}

.v-card__title {
    font-weight: 500;
    font-size: 1.125rem;
}

.v-alert {
    border-left: 4px solid;
}
</style>